export const EXPIRY_STATUS_ACTIVE = 'active';
export const EXPIRY_STATUS_EXPIRED = 'expired';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const pad = value => `${value}`.padStart(2, '0');

export const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
};

const parseExpiryDate = expiresAt => {
  if (!expiresAt) {
    return null;
  }

  const expiryDate = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
  return Number.isNaN(expiryDate.getTime()) ? null : expiryDate;
};

export const roundExpiryDateToNextHour = value => {
  const expiryDate = parseExpiryDate(value);
  if (!expiryDate) {
    return null;
  }

  const rounded = new Date(expiryDate.getTime());
  const hasPartialHour =
    rounded.getMinutes() > 0 || rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0;

  if (hasPartialHour) {
    rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
  } else {
    rounded.setMinutes(0, 0, 0);
  }

  return rounded;
};

export const getDefaultExpiryDateTime = (now = new Date()) => {
  const nowDate = parseExpiryDate(now) || new Date();
  const rounded = roundExpiryDateToNextHour(nowDate);

  if (!rounded) {
    return null;
  }

  if (rounded.getTime() <= nowDate.getTime()) {
    rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
  }

  return rounded;
};

export const getListingPublicData = listingOrPublicData => {
  return listingOrPublicData?.attributes?.publicData || listingOrPublicData || {};
};

export const getListingExpiresAt = listingOrPublicData => {
  return getListingPublicData(listingOrPublicData)?.expiresAt || null;
};

export const isExpiryInPast = (expiresAt, now = Date.now()) => {
  const expiryDate = parseExpiryDate(expiresAt);
  return expiryDate ? expiryDate.getTime() <= now : false;
};

export const isListingExpired = (listingOrPublicData, now = Date.now()) => {
  const publicData = getListingPublicData(listingOrPublicData);
  return (
    publicData?.expiryStatus === EXPIRY_STATUS_EXPIRED || isExpiryInPast(publicData.expiresAt, now)
  );
};

export const datetimeLocalToISOString = value => {
  const expiryDate = roundExpiryDateToNextHour(value);
  return expiryDate ? expiryDate.toISOString() : null;
};

export const toDatetimeLocalValue = value => {
  const date = parseExpiryDate(value);
  if (!date) {
    return '';
  }

  return [
    date.getFullYear(),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes()),
  ].join('');
};

export const normalizeExpiryDateTimeLocalValue = value => {
  if (!value) {
    return value;
  }

  const rounded = roundExpiryDateToNextHour(value);
  return rounded ? toDatetimeLocalValue(rounded) : value;
};

export const formatExpiryDateTime = (expiresAt, locale) => {
  const expiryDate = parseExpiryDate(expiresAt);
  if (!expiryDate) {
    return null;
  }

  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(expiryDate);
  } catch (e) {
    return expiryDate.toLocaleString(locale);
  }
};

export const formatExpiryCountdown = (expiresAt, now = Date.now()) => {
  const expiryDate = parseExpiryDate(expiresAt);
  if (!expiryDate) {
    return null;
  }

  const diff = expiryDate.getTime() - now;
  if (diff <= 0) {
    return 'Expired';
  }

  const days = Math.floor(diff / DAY);
  const hours = Math.floor((diff % DAY) / HOUR);
  const minutes = Math.max(1, Math.ceil((diff % HOUR) / MINUTE));

  if (days > 0) {
    return `Expires in ${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `Expires in ${hours}h ${minutes}m`;
  }
  return `Expires in ${minutes}m`;
};

export const getExpiryTooltip = expiresAt => {
  const formattedDate = formatExpiryDateTime(expiresAt);
  return formattedDate ? `Expires ${formattedDate}` : null;
};
